import React, { useState } from 'react';
import { 
  Table, Button, Input, Message, Icon, Confirm, Header, 
  Dropdown, Checkbox, Form, Modal
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { DiscountRule } from '../../app/models/CompanySportVenueAsset';

interface Props {
  assetId: string;
  discountRules: DiscountRule[];
  onAdd: (rule: Omit<DiscountRule, 'id'>) => void;
  onUpdate: (rule: DiscountRule) => void;
  // Updated to handle both saved (ID) and unsaved (description) rules
  onDelete: (ruleId?: string, description?: string) => void;
  isNewAsset: boolean;
}

const DiscountRulesSection: React.FC<Props> = ({
  assetId,
  discountRules,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<DiscountRule | null>(null);
  
  // Unique UI key for deletion tracking (ID or description-based string)
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);

  const [formData, setFormData] = useState<Omit<DiscountRule, 'id' | 'companySportVenueAssetId'>>({
    discountType: 1,
    discountValue: 0,
    priority: 0,
    isStackable: false,
    isActive: true,
    description: '',
    dayOfWeek: undefined,
    timeFrom: undefined,
    timeTo: undefined
  });

  // Generates a stable key for React rendering and tracking
  const getRuleKey = (rule: DiscountRule): string => {
    return rule.id || `temp-${rule.description}`;
  };

  const dayOfWeekOptions = [
    { key: 'any', text: t('discountRules.anyDay'), value: null },
    { key: 0, text: t('workingHours.days.sunday'), value: 0 },
    { key: 1, text: t('workingHours.days.monday'), value: 1 },
    { key: 2, text: t('workingHours.days.tuesday'), value: 2 },
    { key: 3, text: t('workingHours.days.wednesday'), value: 3 },
    { key: 4, text: t('workingHours.days.thursday'), value: 4 },
    { key: 5, text: t('workingHours.days.friday'), value: 5 },
    { key: 6, text: t('workingHours.days.saturday'), value: 6 },
  ];

  const discountTypeOptions = [
    { key: 1, text: t('discountRules.percentage'), value: 1 },
    { key: 2, text: t('discountRules.fixedAmount'), value: 2 },
  ];

  const handleOpenModal = (rule?: DiscountRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        dayOfWeek: rule.dayOfWeek,
        timeFrom: rule.timeFrom,
        timeTo: rule.timeTo,
        validFrom: rule.validFrom,
        validTo: rule.validTo,
        minCapacityUnits: rule.minCapacityUnits,
        maxCapacityUnits: rule.maxCapacityUnits,
        discountType: rule.discountType,
        discountValue: rule.discountValue,
        priority: rule.priority,
        isStackable: rule.isStackable,
        isActive: rule.isActive,
        description: rule.description || ''
      });
    } else {
      setEditingRule(null);
      setFormData({
        discountType: 1,
        discountValue: 0,
        priority: 0,
        isStackable: false,
        isActive: true,
        description: '',
        dayOfWeek: undefined,
        timeFrom: undefined,
        timeTo: undefined
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.description || formData.discountValue <= 0) return;

    if (editingRule) {
      onUpdate({ ...editingRule, ...formData });
    } else {
      onAdd({ companySportVenueAssetId: assetId, ...formData });
    }

    setModalOpen(false);
    setEditingRule(null);
  };

  const handleDelete = () => {
    if (!deleteConfirmKey) return;
    
    const ruleToDelete = discountRules.find(r => getRuleKey(r) === deleteConfirmKey);
    
    if (ruleToDelete) {
      // Pass ID if available, otherwise description for local filtering
      onDelete(ruleToDelete.id ?? undefined, ruleToDelete.description);
    }
    
    setDeleteConfirmKey(null);
  };

  const formatDiscountDescription = (rule: DiscountRule): string => {
    let parts: string[] = [];
    if (rule.discountType === 1) parts.push(`${rule.discountValue}% ${t('discountRules.off')}`);
    else parts.push(`$${rule.discountValue} ${t('discountRules.off')}`);

    if (rule.dayOfWeek !== null && rule.dayOfWeek !== undefined) {
      const dayOption = dayOfWeekOptions.find(d => d.value === rule.dayOfWeek);
      parts.push(dayOption?.text || '');
    }
    if (rule.timeFrom && rule.timeTo) parts.push(`${rule.timeFrom}-${rule.timeTo}`);
    
    return parts.join(' | ');
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <Header as="h5"><Icon name="tags" />{t('assetForm.discountRules')}</Header>

      <Message info size="small"><p>{t('assetForm.discountRulesHelp')}</p></Message>

      <Button
        color="green" icon="add" type="button"
        content={t('assetForm.addDiscountRule')}
        onClick={() => handleOpenModal()}
        style={{ marginBottom: '15px' }}
      />

      {discountRules.length > 0 ? (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('discountRules.description')}</Table.HeaderCell>
              <Table.HeaderCell>{t('discountRules.summary')}</Table.HeaderCell>
              <Table.HeaderCell width={2}>{t('common.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {discountRules.sort((a, b) => b.priority - a.priority).map((rule) => {
              const ruleKey = getRuleKey(rule);
              return (
                <Table.Row key={ruleKey}>
                  <Table.Cell><strong>{rule.description}</strong></Table.Cell>
                  <Table.Cell>{formatDiscountDescription(rule)}</Table.Cell>
                  <Table.Cell>
                    <Button.Group size="tiny">
                      <Button type="button" color="blue" icon="edit" onClick={() => handleOpenModal(rule)} />
                      <Button type="button" color="red" icon="trash" onClick={() => setDeleteConfirmKey(ruleKey)} />
                    </Button.Group>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : (
        <Message warning content={t('assetForm.noDiscountRules')} />
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="large">
        <Modal.Header>{editingRule ? t('assetForm.editDiscountRule') : t('assetForm.addDiscountRule')}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field required>
              <label>{t('discountRules.description')}</label>
              <Input
                placeholder={t('discountRules.descriptionPlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Field>

            <Form.Group widths="equal">
              <Form.Field required>
                <label>{t('discountRules.discountType')}</label>
                <Dropdown selection options={discountTypeOptions} value={formData.discountType}
                  onChange={(_, data) => setFormData({ ...formData, discountType: data.value as number })} />
              </Form.Field>
              <Form.Field required>
                <label>{t('discountRules.discountValue')}</label>
                <Input type="number" min="0" step={formData.discountType === 1 ? "1" : "0.01"}
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  label={formData.discountType === 1 ? '%' : '$'} labelPosition="right" />
              </Form.Field>
            </Form.Group>

            <Header as="h5" dividing>{t('discountRules.timeConditions')}</Header>
            <Form.Field>
              <label>{t('discountRules.dayOfWeek')}</label>
              <Dropdown selection options={dayOfWeekOptions as any} value={formData.dayOfWeek ?? null as any}
                onChange={(_, data) => setFormData({ ...formData, dayOfWeek: data.value as number | undefined })} />
            </Form.Field>

            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('discountRules.timeFrom')}</label>
                <Input type="time" value={formData.timeFrom || ''} 
                  onChange={(e) => setFormData({ ...formData, timeFrom: e.target.value || undefined })} />
              </Form.Field>
              <Form.Field>
                <label>{t('discountRules.timeTo')}</label>
                <Input type="time" value={formData.timeTo || ''} 
                  onChange={(e) => setFormData({ ...formData, timeTo: e.target.value || undefined })} />
              </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('discountRules.validFrom')}</label>
                <Input type="date" 
                  value={formData.validFrom ? new Date(formData.validFrom).toISOString().split('T')[0] : ''} 
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value ? new Date(e.target.value) : undefined })} />
              </Form.Field>
              <Form.Field>
                <label>{t('discountRules.validTo')}</label>
                <Input type="date" 
                  value={formData.validTo ? new Date(formData.validTo).toISOString().split('T')[0] : ''} 
                  onChange={(e) => setFormData({ ...formData, validTo: e.target.value ? new Date(e.target.value) : undefined })} />
              </Form.Field>
            </Form.Group>

            <Header as="h5" dividing>{t('discountRules.capacityConditions')}</Header>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('discountRules.minCapacity')}</label>
                <Input type="number" value={formData.minCapacityUnits || ''} 
                  onChange={(e) => setFormData({ ...formData, minCapacityUnits: e.target.value ? Number(e.target.value) : undefined })} />
              </Form.Field>
              <Form.Field>
                <label>{t('discountRules.maxCapacity')}</label>
                <Input type="number" value={formData.maxCapacityUnits || ''} 
                  onChange={(e) => setFormData({ ...formData, maxCapacityUnits: e.target.value ? Number(e.target.value) : undefined })} />
              </Form.Field>
            </Form.Group>

            <Header as="h5" dividing>{t('discountRules.applicationRules')}</Header>
            <Form.Field>
              <label>{t('discountRules.priority')}</label>
              <Input type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })} />
            </Form.Field>
            <Form.Field>
              <Checkbox label={t('discountRules.isStackable')} checked={formData.isStackable} 
                onChange={(_, data) => setFormData({ ...formData, isStackable: !!data.checked })} />
            </Form.Field>
            <Form.Field>
              <Checkbox label={t('discountRules.isActive')} checked={formData.isActive} 
                onChange={(_, data) => setFormData({ ...formData, isActive: !!data.checked })} />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button type="button" onClick={() => setModalOpen(false)} content={t('common.cancel')} />
          <Button type="button" positive onClick={handleSubmit} content={t('common.save')} />
        </Modal.Actions>
      </Modal>

      <Confirm
        open={deleteConfirmKey !== null}
        header={t('assetForm.deleteDiscountRule')}
        content={t('assetForm.deleteDiscountRuleConfirm')}
        onCancel={() => setDeleteConfirmKey(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default DiscountRulesSection;